import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  InputNumber,
  Button,
  Table,
  Tag,
  Typography,
  message,
  Spin,
  Empty,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  GiftOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  DeleteOutlined,
  UserOutlined,
  MailOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { giftCertificateService } from '../services/api';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const formatNPR = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'NPR',
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

const statusColors = {
  active: 'green',
  redeemed: 'blue',
  expired: 'default',
  partially_redeemed: 'orange',
};

const GiftCertificate = () => {
  const [activeTab, setActiveTab] = useState('purchase');
  const [purchaseForm] = Form.useForm();
  const [redeemForm] = Form.useForm();
  const [purchasing, setPurchasing] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [certificatesLoading, setCertificatesLoading] = useState(true);
  const [purchasedCode, setPurchasedCode] = useState(null);
  const copyRef = useRef(null);

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    setCertificatesLoading(true);
    try {
      const response = await giftCertificateService.getCertificates();
      const data = response.data?.data || response.data || [];
      setCertificates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching gift certificates:', error);
    } finally {
      setCertificatesLoading(false);
    }
  };

  const handlePurchase = async (values) => {
    setPurchasing(true);
    try {
      const response = await giftCertificateService.purchase(values);
      const code = response.data?.code || response.data?.data?.code;
      setPurchasedCode(code);
      message.success('Gift certificate purchased successfully!');
      purchaseForm.resetFields();
      fetchCertificates();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to purchase gift certificate.';
      message.error(msg);
    } finally {
      setPurchasing(false);
    }
  };

  const handleRedeem = async (values) => {
    setRedeeming(true);
    try {
      const response = await giftCertificateService.redeem(values.code);
      const amount = response.data?.amount || response.data?.data?.amount || response.data?.message;
      message.success(
        `Gift certificate redeemed successfully!${amount ? ` Amount credited: ${formatNPR(amount)}` : ''}`,
      );
      redeemForm.resetFields();
      fetchCertificates();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to redeem gift certificate.';
      message.error(msg);
    } finally {
      setRedeeming(false);
    }
  };

  const handleCopyCode = () => {
    if (purchasedCode && copyRef.current) {
      copyRef.current.select();
      navigator.clipboard?.writeText(purchasedCode);
      message.success('Code copied to clipboard!');
    }
  };

  const handleDelete = async (id) => {
    try {
      await giftCertificateService.delete(id);
      message.success('Gift certificate deleted.');
      fetchCertificates();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete.');
    }
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (code) => (
        <Text code strong style={{ fontSize: '0.85rem' }}>
          {code}
        </Text>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => formatNPR(amount),
      width: 120,
    },
    {
      title: 'Remaining',
      dataIndex: 'remaining_amount',
      key: 'remaining_amount',
      render: (val) => {
        if (val === undefined || val === null) return '-';
        return formatNPR(val);
      },
      width: 120,
    },
    {
      title: 'Recipient',
      key: 'recipient',
      render: (_, record) => (
        <div>
          {record.recipient_name && <div>{record.recipient_name}</div>}
          {record.recipient_email && (
            <Text type="secondary" style={{ fontSize: '0.8rem' }}>
              {record.recipient_email}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={statusColors[status] || 'default'}>{(status || '').replace(/_/g, ' ')}</Tag>
      ),
      width: 120,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d) => (d ? new Date(d).toLocaleDateString() : '-'),
      width: 100,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.id)}
          size="small"
        />
      ),
      width: 60,
    },
  ];

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <div className="customer-page-header">
        <Title level={2}>
          <GiftOutlined style={{ marginRight: 8 }} />
          Gift Certificates
        </Title>
        <Paragraph>Purchase gift certificates or redeem a code.</Paragraph>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'purchase',
            label: (
              <span>
                <GiftOutlined /> Purchase
              </span>
            ),
            children: (
              <Row gutter={[24, 24]}>
                <Col xs={24} md={12}>
                  <Card className="customer-card" title="Buy a Gift Certificate">
                    <Form
                      form={purchaseForm}
                      layout="vertical"
                      onFinish={handlePurchase}
                      size="large"
                    >
                      <Form.Item
                        name="amount"
                        label="Amount"
                        rules={[
                          { required: true, message: 'Please enter an amount' },
                          {
                            type: 'number',
                            min: 1,
                            message: 'Amount must be at least 1',
                          },
                        ]}
                      >
                        <InputNumber
                          min={1}
                          prefix="NPR"
                          style={{ width: '100%' }}
                          placeholder="1000"
                        />
                      </Form.Item>

                      <Form.Item
                        name="recipient_email"
                        label="Recipient Email"
                        rules={[{ type: 'email', message: 'Enter a valid email' }]}
                      >
                        <Input prefix={<MailOutlined />} placeholder="recipient@example.com" />
                      </Form.Item>

                      <Form.Item name="recipient_name" label="Recipient Name">
                        <Input prefix={<UserOutlined />} placeholder="John Doe" />
                      </Form.Item>

                      <Form.Item name="message" label="Personal Message">
                        <TextArea
                          rows={3}
                          placeholder="Happy Birthday!"
                          maxLength={500}
                          showCount
                        />
                      </Form.Item>

                      <Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={purchasing}
                          icon={<GiftOutlined />}
                          size="large"
                          block
                        >
                          Purchase Gift Certificate
                        </Button>
                      </Form.Item>
                    </Form>
                  </Card>
                </Col>

                <Col xs={24} md={12}>
                  {purchasedCode ? (
                    <Card className="customer-card" title="Purchase Successful!">
                      <div style={{ textAlign: 'center', padding: '24px 0' }}>
                        <CheckCircleOutlined
                          style={{
                            fontSize: 48,
                            color: '#52c41a',
                            marginBottom: 16,
                          }}
                        />
                        <Title level={4}>Gift Certificate Code</Title>
                        <div
                          style={{
                            background: '#f6ffed',
                            border: '1px solid #b7eb8f',
                            borderRadius: 8,
                            padding: '16px 24px',
                            margin: '16px 0',
                          }}
                        >
                          <Text
                            strong
                            style={{
                              fontSize: '1.5rem',
                              letterSpacing: 4,
                              fontFamily: 'monospace',
                            }}
                          >
                            {purchasedCode}
                          </Text>
                        </div>
                        <input
                          ref={copyRef}
                          value={purchasedCode}
                          readOnly
                          style={{ position: 'absolute', left: -9999 }}
                        />
                        <Button
                          type="primary"
                          icon={<CopyOutlined />}
                          onClick={handleCopyCode}
                          size="large"
                        >
                          Copy Code
                        </Button>
                      </div>
                    </Card>
                  ) : (
                    <Card className="customer-card" title="Gift Certificate Info">
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 24,
                          padding: '16px 0',
                        }}
                      >
                        <div>
                          <Statistic
                            title="How it works"
                            value="Purchase"
                            prefix={<GiftOutlined />}
                          />
                          <Paragraph type="secondary" style={{ marginTop: 8, fontSize: '0.9rem' }}>
                            Enter the amount and recipient details. After purchase, you'll receive a
                            unique code to share.
                          </Paragraph>
                        </div>
                        <div>
                          <Statistic
                            title="Redeem"
                            value="Use Anywhere"
                            prefix={<CheckCircleOutlined />}
                          />
                          <Paragraph type="secondary" style={{ marginTop: 8, fontSize: '0.9rem' }}>
                            Recipients can redeem the code for store credit toward any product or
                            service.
                          </Paragraph>
                        </div>
                      </div>
                    </Card>
                  )}
                </Col>
              </Row>
            ),
          },
          {
            key: 'redeem',
            label: (
              <span>
                <CheckCircleOutlined /> Redeem
              </span>
            ),
            children: (
              <Row gutter={[24, 24]}>
                <Col xs={24} md={12}>
                  <Card className="customer-card" title="Redeem a Gift Certificate">
                    <Form form={redeemForm} layout="vertical" onFinish={handleRedeem} size="large">
                      <Form.Item
                        name="code"
                        label="Gift Certificate Code"
                        rules={[{ required: true, message: 'Please enter the code' }]}
                      >
                        <Input
                          prefix={<KeyOutlined />}
                          placeholder="Enter your code"
                          style={{ fontFamily: 'monospace' }}
                        />
                      </Form.Item>

                      <Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={redeeming}
                          icon={<CheckCircleOutlined />}
                          size="large"
                          block
                        >
                          Redeem
                        </Button>
                      </Form.Item>
                    </Form>
                  </Card>
                </Col>

                <Col xs={24} md={12}>
                  <Card className="customer-card" title="How Redemption Works">
                    <div style={{ padding: '16px 0' }}>
                      <Paragraph>
                        Enter the gift certificate code you received to apply the balance to your
                        account wallet.
                      </Paragraph>
                      <ul style={{ paddingLeft: 20, lineHeight: 2.2 }}>
                        <li>Codes are case-sensitive</li>
                        <li>The full balance is credited to your wallet</li>
                        <li>Gift certificates cannot be reused once redeemed</li>
                      </ul>
                    </div>
                  </Card>
                </Col>
              </Row>
            ),
          },
        ]}
      />

      <Card
        className="customer-card"
        title={
          <span style={{ fontWeight: 600 }}>
            <GiftOutlined style={{ marginRight: 8 }} />
            My Gift Certificates
          </span>
        }
        extra={
          <Button
            className="customer-btn-secondary"
            icon={<ReloadOutlined />}
            size="small"
            onClick={fetchCertificates}
          >
            Refresh
          </Button>
        }
        style={{ marginTop: 24 }}
      >
        {certificatesLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={certificates}
            rowKey="id"
            pagination={{ pageSize: 10, size: 'small' }}
            locale={{
              emptyText: <Empty description="No gift certificates yet. Purchase one above!" />,
            }}
          />
        )}
      </Card>
    </div>
  );
};

export default GiftCertificate;
